import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';

import { ThemedText } from '@/components/ThemedText';
import { useScale } from '@/hooks/useScale';

export default function DebugLocalAudioPlayer() {
  const styles = useStyles();

  const audioSource = useMemo(
    () => require('@/assets/audio/30_Seconds.mp3'),
    [],
  );

  const audioPlayer = useVideoPlayer(audioSource, (player) => {
    player.loop = false;
    player.timeUpdateEventInterval = 0.25;
    player.muted = false;
    player.volume = 1.0;
  });

  const [audioIsReady, setAudioIsReady] = useState(false);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  const [audioCurrentTimeSec, setAudioCurrentTimeSec] = useState(0);
  const [audioDurationSec, setAudioDurationSec] = useState(0);
  const audioDurationRef = useRef(0);
  const [audioStatus, setAudioStatus] = useState<string>('unknown');

  useEffect(() => {
    const statusSub = audioPlayer.addListener('statusChange', (payload) => {
      setAudioIsReady(payload.status === 'readyToPlay');
      setAudioStatus(payload.status);
      if (payload.status === 'error') {
        console.error('[Explore] Audio player error:', payload.error);
      }
    });

    const playingSub = audioPlayer.addListener('playingChange', (payload) => {
      setAudioIsPlaying(payload.isPlaying);
    });

    const sourceLoadSub = audioPlayer.addListener('sourceLoad', () => {
      const duration = Number.isFinite(audioPlayer.duration)
        ? audioPlayer.duration
        : 0;
      audioDurationRef.current = duration;
      setAudioDurationSec(duration);
    });

    const timeSub = audioPlayer.addListener('timeUpdate', (payload) => {
      setAudioCurrentTimeSec(payload.currentTime);
      if (audioDurationRef.current === 0) {
        const duration = Number.isFinite(audioPlayer.duration)
          ? audioPlayer.duration
          : 0;
        if (duration > 0) {
          audioDurationRef.current = duration;
          setAudioDurationSec(duration);
        }
      }
    });

    return () => {
      statusSub.remove();
      playingSub.remove();
      sourceLoadSub.remove();
      timeSub.remove();
    };
  }, [audioPlayer]);

  const toggleAudio = () => {
    if (audioPlayer.playing) {
      audioPlayer.pause();
    } else {
      audioPlayer.play();
    }
  };

  const stopAudio = () => {
    audioPlayer.pause();
    audioPlayer.currentTime = 0;
  };

  return (
    <>
      <ThemedText style={styles.mutedText}>
        {audioIsReady
          ? `30_Seconds.mp3 • ${formatSeconds(audioCurrentTimeSec)} / ${formatSeconds(audioDurationSec)}`
          : `Loading… (status: ${audioStatus})`}
      </ThemedText>

      <View style={styles.videoContainer}>
        <VideoView
          player={audioPlayer}
          nativeControls={true}
          contentFit="contain"
        />
      </View>

      <View style={styles.row}>
        <Pressable
          onPress={toggleAudio}
          disabled={!audioIsReady}
          style={({ focused }) => [
            styles.button,
            focused && styles.buttonFocused,
            !audioIsReady && styles.buttonDisabled,
          ]}
        >
          <ThemedText style={styles.buttonText}>
            {audioIsPlaying ? 'Pause' : 'Play'}
          </ThemedText>
        </Pressable>

        <Pressable
          onPress={stopAudio}
          disabled={!audioIsReady}
          style={({ focused }) => [
            styles.button,
            focused && styles.buttonFocused,
            !audioIsReady && styles.buttonDisabled,
          ]}
        >
          <ThemedText style={styles.buttonText}>Stop</ThemedText>
        </Pressable>
      </View>
    </>
  );
}

function formatSeconds(seconds: number) {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function useStyles() {
  const scale = useScale();
  return StyleSheet.create({
    mutedText: {
      color: 'rgba(255,255,255,0.7)',
    },
    videoContainer: {
      width: '100%',
    },
    row: {
      flexDirection: 'row',
      gap: 12 * scale,
      alignItems: 'center',
    },
    button: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 18 * scale,
      borderRadius: 10 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    buttonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    buttonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    buttonDisabled: {
      opacity: 0.4,
    },
  });
}
